import { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useConfig } from '../context/ConfigContext';
import { startAdminSession, getAdminSession, clearAdminActivity, touchAdminActivity, initializeDefaultUser } from '../utils/authServiceUtil';
import { POS_TABS } from '../constants/posConst';
import { loadingSpinner } from '../utils/bookingUtils';
import BrandLogo from './BrandLogo';
import ComingSoonScreen from './ComingSoonScreen';
import PosHeader from './PosHeader';
import PosInventory from './PosInventory';
import PosDashboard from './PosDashboard';
import PosSales from './PosSales';
import PosAuditTrail from './PosAuditTrail';
import '../css/Pos.css';

/**
 * Point of Sale (/pos) — the counter app.
 *
 * SIGN-IN. The app already signs every visitor in as a shared anonymous user
 * at startup (AuthGuard), so `auth.currentUser` being set proves nothing here.
 * This screen therefore keeps its own gate, exactly as AdminPage does: a real
 * Firebase Auth email/password sign-in, remembered through the same
 * localStorage admin session so a refresh mid-shift doesn't log the counter
 * out. Signing out returns to the shared user rather than leaving the app
 * unauthenticated, which would break every other listener on the page.
 *
 * @returns {JSX.Element}
 */
function PosPage() {
  const { config, loading: configLoading } = useConfig();
  const idleTimeoutEnabled = config?.adminIdleTimeoutEnabled !== false;
  // Config -> System -> Point of Sale. `!== false` rather than a truthiness
  // check: a config saved before this setting existed has no such key, and
  // that has to keep opening the till rather than silently closing it on a
  // club already using it.
  // `=== true`, not `!== false`: the till is closed until an admin opens it,
  // so a config saved before this setting existed — where the key is simply
  // absent — serves the Coming Soon page rather than a half-set-up counter.
  const posEnabled = config?.posEnabled === true;

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [signedInEmail, setSignedInEmail] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [tab, setTab] = useState(POS_TABS[0].key);

  // Restores a session that is still inside its idle window, so refreshing
  // the till does not mean signing in again.
  useEffect(() => {
    const session = getAdminSession(idleTimeoutEnabled);
    if (session?.email) {
      setSignedInEmail(session.email);
      setIsAuthenticated(true);
    }
  }, [idleTimeoutEnabled]);

  // Keeps the idle window open while the counter is in use.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const bump = () => touchAdminActivity();
    window.addEventListener('click', bump);
    window.addEventListener('keydown', bump);
    return () => {
      window.removeEventListener('click', bump);
      window.removeEventListener('keydown', bump);
    };
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!email || !password) {
      setLoginError('Please enter both email and password.');
      return;
    }
    setSigningIn(true);
    setLoginError('');
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const authenticatedEmail = credential.user?.email || email;
      setSignedInEmail(authenticatedEmail);
      setIsAuthenticated(true);
      startAdminSession(authenticatedEmail);
      setEmail('');
      setPassword('');
    } catch (error) {
      console.error('POS sign-in failed:', error.code);
      setPassword('');
      // Same three buckets AdminPage uses — a wrong password and an unknown
      // account deliberately read alike, so the form cannot be used to find
      // out which staff emails exist.
      if (['auth/invalid-credential', 'auth/user-not-found', 'auth/wrong-password'].includes(error.code)) {
        setLoginError('Invalid email or password.');
      } else if (error.code === 'auth/invalid-email') {
        setLoginError('Please enter a valid email format.');
      } else {
        setLoginError('Login failed. Please try again.');
      }
    } finally {
      setSigningIn(false);
    }
  };

  const handleLogout = async () => {
    clearAdminActivity();
    setIsAuthenticated(false);
    setSignedInEmail('');
    try {
      await signOut(auth);
      // Back to the shared anonymous user. Without this the rest of the app
      // is left signed out, and every rule in database.rules.json requires
      // auth != null — the booking screens would start failing behind us.
      await initializeDefaultUser();
    } catch (error) {
      console.error('POS sign-out failed:', error);
    }
  };

  // Whether the till is open at all is a config setting, so nothing renders
  // until it has resolved — deciding against the default would flash the
  // wrong screen at a club on either side of the switch.
  if (configLoading) {
    return loadingSpinner('', config?.logoUrl);
  }

  // Checked BEFORE the sign-in gate: a closed till should not ask staff for
  // credentials it is not going to use.
  if (!posEnabled) {
    return (
      <ComingSoonScreen
        title="Point of Sale is coming soon"
        message="The counter app isn&rsquo;t open yet. It will appear here once the club switches it on."
        brandName={config?.courtName || ''}
        logoUrl={config?.logoUrl}
      />
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="pos-login-page">
        <form className="pos-login-card" onSubmit={handleLogin}>
          <BrandLogo src={config?.logoUrl} alt={config?.courtName || 'Court'} className="pos-login-logo" />
          <h1 className="pos-login-title">{config?.courtName || 'Point of Sale'}</h1>
          <p className="pos-login-sub">Staff sign-in</p>

          <label className="pos-field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              disabled={signingIn}
            />
          </label>
          <label className="pos-field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={signingIn}
            />
          </label>

          {loginError && <p className="pos-login-error">{loginError}</p>}

          <button type="submit" className="pos-btn pos-btn-primary" disabled={signingIn}>
            {signingIn ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    );
  }

  const renderTab = () => {
    switch (tab) {
      case 'inventory': return <PosInventory />;
      case 'sales': return <PosSales />;
      case 'audit': return <PosAuditTrail />;
      case 'dashboard':
      default: return <PosDashboard />;
    }
  };

  return (
    <div className="pos-page">
      {/* Brand, screen tabs and the staff account all live in the header now
          — it is the booking screen's header (see PosHeader), which carries
          its own nav and collapses it on narrow screens. */}
      <PosHeader
        courtName={config?.courtName || 'Point of Sale'}
        logoUrl={config?.logoUrl}
        tabs={POS_TABS}
        activeTab={tab}
        onTabChange={setTab}
        signedInEmail={signedInEmail}
        onSignOut={handleLogout}
      />

      <main className="pos-main">{renderTab()}</main>
    </div>
  );
}

export default PosPage;
